import {
  applyCaseVariablesToTemplate,
  getDisplayTemplateTitle,
  normalizeTemplateSide,
  normalizeTemplateTitle,
  resolveCaseTypeKey,
  resolveTemplateContent,
  type SystemTemplateDoc,
} from './systemTemplates';
import {
  buildExemplarGuidedAiInstruction,
  buildTemplateGuidedAiInstruction,
  buildTemplateReferenceFromResolved,
  type TemplateReferencePayload,
} from './templateAiDrafting';
import type { DraftingStreamOptions } from './ai';
import { getDraftingDateContext } from './draftingDateContext';

export interface DraftingFormattingBundle {
  templateOptions?: DraftingStreamOptions;
  /** When set, prepend to the task instruction so output matches template/exemplar layout. */
  formattingInstruction?: string;
}

export function isSparseDraftHtml(draftHtml: string): boolean {
  const trimmed = draftHtml.trim();
  return (
    !trimmed ||
    trimmed === '<p></p>' ||
    trimmed.length < 120 ||
    trimmed.includes('ai-generating-placeholder')
  );
}

export interface FormattingExemplarInput {
  caseData: Record<string, unknown> | null | undefined;
  documentTitle: string;
  documentType?: string;
  systemTemplates: SystemTemplateDoc[];
  workspaceTemplates?: SystemTemplateDoc[];
  /** Skip the linked (empty) template when picking an exemplar */
  excludeTemplateId?: string;
}

function templateHasBody(tpl: SystemTemplateDoc): boolean {
  const resolved = resolveTemplateContent(tpl);
  if (resolved.html.trim()) return true;
  return Boolean(resolved.tabs?.some((tab) => (tab.content ?? '').trim()));
}

function titlesExactMatch(a: string, b: string): boolean {
  return normalizeTemplateTitle(a) === normalizeTemplateTitle(b);
}

function titlesFuzzyMatch(a: string, b: string): boolean {
  const left = normalizeTemplateTitle(a);
  const right = normalizeTemplateTitle(b);
  if (!left || !right) return false;
  if (left === right) return true;
  if (left.includes(right) || right.includes(left)) return true;

  const words = left.split(/\s+/).filter((word) => word.length > 3);
  const rightWords = right.split(/\s+/);
  return words.some((word) =>
    rightWords.some((other) => other.includes(word) || word.includes(other)),
  );
}

function scoreExemplar(
  tpl: SystemTemplateDoc,
  input: FormattingExemplarInput,
): number {
  if (!templateHasBody(tpl)) return 0;
  if (input.excludeTemplateId && tpl.id === input.excludeTemplateId) return 0;

  const caseSide = normalizeTemplateSide(
    (input.caseData?.representingSide as string) ||
      (input.caseData?.sideRepresented as string),
  );
  const tplSides = tpl.templateSides ?? [];
  const sameSide =
    !tplSides.length || (caseSide != null && tplSides.includes(caseSide));
  const oppositeSide =
    caseSide != null &&
    tplSides.length > 0 &&
    !sameSide &&
    ((caseSide === 'plaintiff' && tplSides.includes('defendant')) ||
      (caseSide === 'defendant' && tplSides.includes('plaintiff')));

  const caseTypeKey = resolveCaseTypeKey(input.caseData);
  const tplTypes = tpl.templateCaseTypes ?? [];
  const sameCaseType =
    !tplTypes.length ||
    tplTypes.some((type) => resolveCaseTypeKey({ matterType: type }) === caseTypeKey);

  if (!sameCaseType) return 0;

  const docTitle = input.documentTitle.trim();
  const docType = (input.documentType || docTitle).trim();
  const tplTitle = getDisplayTemplateTitle(tpl);
  const tplDocType = (tpl.documentType || tplTitle).trim();

  if (sameSide) {
    if (titlesExactMatch(docTitle, tplTitle) || titlesExactMatch(docType, tplDocType)) {
      return 100;
    }
    if (titlesFuzzyMatch(docTitle, tplTitle) || titlesFuzzyMatch(docType, tplDocType)) {
      return 80;
    }
    return 60;
  }

  if (oppositeSide) return 40;
  return 0;
}

/** @internal Exported for deterministic scoring tests. */
export function scoreFormattingExemplar(
  tpl: SystemTemplateDoc,
  input: FormattingExemplarInput,
): number {
  return scoreExemplar(tpl, input);
}

/** Pick the best LexGAT formatting exemplar when no exact template body exists. */
export function resolveFormattingExemplar(
  input: FormattingExemplarInput,
): SystemTemplateDoc | null {
  if (!input.caseData) return null;

  const candidates = [...input.systemTemplates, ...(input.workspaceTemplates ?? [])];

  let best: SystemTemplateDoc | null = null;
  let bestScore = 0;

  for (const tpl of candidates) {
    const score = scoreExemplar(tpl, input);
    if (score > bestScore) {
      best = tpl;
      bestScore = score;
    }
  }

  return bestScore > 0 ? best : null;
}

export function buildExemplarTemplateReference(
  exemplar: SystemTemplateDoc,
  caseData: Record<string, unknown> | null | undefined,
  userData: unknown,
  docTitle: string,
): { html: string; tabs?: SystemTemplateDoc['tabs']; activeTabId?: string; reference: TemplateReferencePayload } {
  const resolved = applyCaseVariablesToTemplate(exemplar, caseData, userData);
  const title = getDisplayTemplateTitle(exemplar) || docTitle;
  return {
    html: resolved.html,
    tabs: resolved.tabs,
    activeTabId: resolved.activeTabId,
    reference: buildTemplateReferenceFromResolved({
      docTitle: title,
      html: resolved.html,
      tabs: resolved.tabs,
    }),
  };
}

/**
 * Resolve template reference + formatting instructions for inline draft, pills, and Lex Counsel.
 * Uses intent text (slash prompt, pill label, counsel message) to pick the best matching template,
 * then falls back to same matter-type exemplar formatting.
 */
export function resolveDraftingFormattingBundle(input: {
  caseData?: Record<string, unknown> | null;
  documentTitle: string;
  /** User task or catalog label — used to match templates (e.g. "reply", "Application for Stay"). */
  intentTitle?: string;
  draftHtml: string;
  systemTemplates: SystemTemplateDoc[];
  workspaceTemplates?: SystemTemplateDoc[];
  userData?: unknown;
}): DraftingFormattingBundle {
  if (!input.caseData || !isSparseDraftHtml(input.draftHtml)) {
    return {};
  }

  const intent = (input.intentTitle || input.documentTitle || '').trim();
  const docTitle = intent || input.documentTitle;

  const scoringInput: FormattingExemplarInput = {
    caseData: input.caseData,
    documentTitle: intent,
    documentType: intent,
    systemTemplates: input.systemTemplates,
    workspaceTemplates: input.workspaceTemplates,
  };

  const candidates = [...input.systemTemplates, ...(input.workspaceTemplates ?? [])];
  let best: SystemTemplateDoc | null = null;
  let bestScore = 0;

  for (const tpl of candidates) {
    const score = scoreExemplar(tpl, scoringInput);
    if (score > bestScore) {
      best = tpl;
      bestScore = score;
    }
  }

  // Same matter/side templates at 40+ when no title match — "other documents" formatting style
  if (!best || bestScore < 40) {
    return {};
  }

  const { reference } = buildExemplarTemplateReference(
    best,
    input.caseData,
    input.userData,
    docTitle,
  );

  const exemplarTitle = getDisplayTemplateTitle(best) || reference.title;
  const formattingInstruction =
    bestScore >= 80
      ? buildTemplateGuidedAiInstruction({
          docTitle,
          caseData: input.caseData,
        })
      : buildExemplarGuidedAiInstruction({
          docTitle,
          exemplarTitle,
          caseData: input.caseData,
        });

  return {
    templateOptions: {
      templateMode: true,
      templateTitle: reference.title,
      templateReference: reference,
    },
    formattingInstruction,
  };
}

export function composeDraftingInstruction(
  taskInstruction: string,
  bundle: DraftingFormattingBundle,
): string {
  const task = taskInstruction.trim();
  const formatting = bundle.formattingInstruction?.trim();
  const dateBlock = getDraftingDateContext();
  if (!formatting) {
    return `${dateBlock}\n\nSpecific task:\n${task}`;
  }
  return `${dateBlock}\n\n${formatting}\n\nSpecific task:\n${task}`;
}

export function resolveEditorDraftingTemplateOptions(input: {
  caseData?: Record<string, unknown> | null;
  documentTitle: string;
  documentType?: string;
  intentTitle?: string;
  draftHtml: string;
  systemTemplates: SystemTemplateDoc[];
  workspaceTemplates?: SystemTemplateDoc[];
  userData?: unknown;
}): DraftingStreamOptions | undefined {
  return resolveDraftingFormattingBundle({
    caseData: input.caseData,
    documentTitle: input.documentTitle,
    intentTitle: input.intentTitle ?? input.documentType ?? input.documentTitle,
    draftHtml: input.draftHtml,
    systemTemplates: input.systemTemplates,
    workspaceTemplates: input.workspaceTemplates,
    userData: input.userData,
  }).templateOptions;
}
